import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const HomeScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const navigate = useNavigate();

  const goToCoachingCenterInvoices = () => {
    navigate("/billing/coaching-center/invoices");
  };

  const goToEnglishQuizBlueprints = () => {
    navigate("/english-quizzes");
  };

  // ======================================================
  // ENGLISH QUIZ ATTEMPT NAVIGATION
  // ======================================================

  // Center Admin
  const goToEnglishQuizAttemptsAdmin = () => {
    navigate("/english-quiz-attempts");
  };

  // Student
  const goToMyEnglishQuizAttempts = () => {
    navigate("/quiz-attempts/my");
  };

  const goToEnglishQuizzes = () => {
    navigate("/student/quizzes");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto max-w-7xl p-6">
        {/* ======================================================
            WELCOME CARD
        ====================================================== */}

        <div className="rounded-2xl bg-white p-8 text-center shadow-lg">
          <h1 className="text-3xl font-bold text-gray-800">
            Welcome, {userInfo?.name}
          </h1>

          <p className="mt-3 text-lg text-gray-600">
            Online Coaching Center Management System
          </p>

          <div className="mt-6 inline-flex items-center rounded-full bg-blue-100 px-5 py-2 font-semibold capitalize text-blue-700">
            Logged in as {userInfo?.role}
          </div>
        </div>

        {/* ======================================================
            PRODUCT ADMIN DASHBOARD
        ====================================================== */}

        {userInfo?.role === "productAdmin" && (
          <div className="mt-10">
            <h2 className="mb-6 text-2xl font-bold text-gray-800">
              Product Administrator Dashboard
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* ==================================================
                  COACHING CENTERS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/coaching-centers")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-indigo-600">
                  Coaching Centers
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage coaching centers.
                </p>
              </button>

              {/* ==================================================
                  USER MANAGEMENT
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/users")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-blue-600">
                  User Management
                </h3>

                <p className="mt-2 text-gray-600">
                  Manage center administrators and students.
                </p>
              </button>

              {/* ==================================================
                  QUESTION BANK SUBJECTS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/question-bank-subjects")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-green-600">
                  Question Bank Subjects
                </h3>

                <p className="mt-2 text-gray-600">
                  Manage subjects used for question banks.
                </p>
              </button>

              {/* ==================================================
                  CHAPTERS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/chapters")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-cyan-600">
                  Chapters
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage question bank chapters.
                </p>
              </button>

              {/* ==================================================
                  ENGLISH QUESTION GROUPS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/english-question-groups")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-amber-600">
                  English Question Groups
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage English question groups.
                </p>
              </button>

              {/* ==================================================
                  ENGLISH QUESTIONS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/english-questions")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-violet-600">
                  English Questions
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage English question bank questions.
                </p>
              </button>

              

              {/* ==================================================
                  QUESTIONS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/questions/create")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-purple-600">
                  Questions
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage question bank questions.
                </p>
              </button>

              {/* ==================================================
                  BILLING DASHBOARD
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/billing/dashboard")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-emerald-600">
                  Billing Dashboard
                </h3>

                <p className="mt-2 text-gray-600">
                  View billing statistics, revenue, outstanding invoices,
                  and center billing information.
                </p>
              </button>

              {/* ==================================================
                  BILLING & INVOICES
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/billing/invoices")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-teal-600">
                  Billing &amp; Invoices
                </h3>

                <p className="mt-2 text-gray-600">
                  View, manage, regenerate, pay, and cancel invoices.
                </p>
              </button>

              {/* ==================================================
                  MONTHLY REVENUE
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/billing/dashboard/monthly-revenue")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-green-600">
                  Monthly Revenue
                </h3>

                <p className="mt-2 text-gray-600">
                  View monthly billing revenue and payment trends.
                </p>
              </button>

              {/* ==================================================
                  OUTSTANDING INVOICES
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/billing/dashboard/outstanding")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-red-600">
                  Outstanding Invoices
                </h3>

                <p className="mt-2 text-gray-600">
                  View unpaid and outstanding coaching center invoices.
                </p>
              </button>

              {/* ==================================================
                  GENERATE INVOICE
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/billing/invoices/generate")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-orange-600">
                  Generate Invoice
                </h3>

                <p className="mt-2 text-gray-600">
                  Open invoice management and generate an invoice for a
                  coaching center.
                </p>
              </button>

              {/* ==================================================
                  BILLING SETTINGS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/coaching-centers")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-pink-600">
                  Billing Settings
                </h3>

                <p className="mt-2 text-gray-600">
                  Select a coaching center and manage its billing settings.
                </p>
              </button>
            </div>
          </div>
        )}

        {/* ======================================================
            CENTER ADMIN DASHBOARD
        ====================================================== */}

        {userInfo?.role === "centerAdmin" && (
          <div className="mt-10">
            <h2 className="mb-6 text-2xl font-bold text-gray-800">
              Center Administrator Dashboard
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* ==================================================
                  USER MANAGEMENT
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/users")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-blue-600">
                  User Management
                </h3>

                <p className="mt-2 text-gray-600">
                  Manage users in your coaching center.
                </p>
              </button>

              {/* ==================================================
                  QUIZZES
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/quizzes")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-blue-600">
                  Quizzes
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage quizzes.
                </p>
              </button>

              {/* ==================================================
                  ENGLISH QUIZZES
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/english-quizzes")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-fuchsia-600">
                  English Quizzes
                </h3>

                <p className="mt-2 text-gray-600">
                  Create and manage English quizzes and view quiz statistics.
                </p>
              </button>

              {/* ==================================================
                  ENGLISH QUIZ BLUEPRINTS
              ================================================== */}

              {/* <button
                type="button"
                onClick={goToEnglishQuizBlueprints}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-indigo-600">
                  English Quiz Blueprints
                </h3>

                <p className="mt-2 text-gray-600">
                  Create, edit, validate, and check availability of English
                  quiz blueprints.
                </p>
              </button> */}

              {/* ==================================================
                  NEW ENGLISH QUIZ ATTEMPT ADMIN
              ================================================== */}

              {/* <button
                type="button"
                onClick={goToEnglishQuizAttemptsAdmin}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-orange-600">
                  English Quiz Attempts
                </h3>

                <p className="mt-2 text-gray-600">
                  View English quiz attempts, student results, and detailed
                  attempt information.
                </p>
              </button> */}

              {/* ==================================================
                  QUIZ REPORTS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/quiz-attempts")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-red-600">
                  Quiz Reports
                </h3>

                <p className="mt-2 text-gray-600">
                  View quiz attempt reports.
                </p>
              </button>

              {/* ==================================================
                  DASHBOARD
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-blue-600">
                  Dashboard
                </h3>

                <p className="mt-2 text-gray-600">
                  Manage your coaching center dashboard.
                </p>
              </button>

              {/* ==================================================
                  ENGLISH QUESTIONS
              ================================================== */}

              {/* <button
                type="button"
                onClick={() => navigate("/english-questions")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-violet-600">
                  English Questions
                </h3>

                <p className="mt-2 text-gray-600">
                  View English question bank questions.
                </p>
              </button> */}

              {/* ==================================================
                  COACHING CENTER INVOICES
              ================================================== */}

              <button
                type="button"
                onClick={goToCoachingCenterInvoices}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-teal-600">
                  My Invoices
                </h3>

                <p className="mt-2 text-gray-600">
                  View your coaching center invoices and payment status.
                </p>
              </button>
            </div>
          </div>
        )}

        {/* ======================================================
            STUDENT DASHBOARD
        ====================================================== */}

        {userInfo?.role === "student" && (
          <div className="mt-10">
            <h2 className="mb-6 text-2xl font-bold text-gray-800">
              Student Dashboard
            </h2>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* ==================================================
                  AVAILABLE QUIZZES
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/student/quizzes")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-blue-600">
                  Available Quizzes
                </h3>

                <p className="mt-2 text-gray-600">
                  View available quizzes and start a new attempt.
                </p>
              </button>

              {/* ==================================================
                  MY NORMAL QUIZ ATTEMPTS
              ================================================== */}

              <button
                type="button"
                onClick={() => navigate("/quiz-attempts/my")}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-green-600">
                  My Attempts
                </h3>

                <p className="mt-2 text-gray-600">
                  View completed quizzes and scores.
                </p>
              </button>

              {/* ==================================================
                  NEW ENGLISH QUIZZES
              ================================================== */}

              {/* <button
                type="button"
                onClick={goToEnglishQuizzes}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-fuchsia-600">
                  Quizzes
                </h3>

                <p className="mt-2 text-gray-600">
                  View available  quizzes and start a new attempt.
                </p>
              </button> */}

              {/* ==================================================
                  NEW ENGLISH QUIZ ATTEMPTS
              ================================================== */}

              {/* <button
                type="button"
                onClick={goToMyEnglishQuizAttempts}
                className="rounded-2xl bg-white p-6 text-left shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <h3 className="text-xl font-bold text-indigo-600">
                  My Quiz Attempts
                </h3>

                <p className="mt-2 text-gray-600">
                  View your quiz attempts, results, scores, and
                  completed quizzes.
                </p>
              </button> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeScreen;